import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate } from "react-router";
import { AmbientBackground } from "../components/AmbientBackground";
import { Glimpse } from "../components/Glimpse";
import { GrowingBranch } from "../components/GrowingBranch";
import { ShieldGlimpse } from "../components/LandingArt";
import { useRegistrationOpen } from "../hooks/useRegistrationOpen";
import { useTheme } from "../hooks/useTheme";
import { getAccessToken, getFaq } from "../lib/api";
import { APP_STORE_URL, GITHUB_URL } from "../lib/links";
import "../styles/landing.css";

// The built-in FAQ, in two groups of three. An admin-managed live FAQ
// replaces it wholesale and renders as one ungrouped list.
const FAQ_GROUPS = [
  { key: "tool", title: "landing.faqGroupTool", numbers: [1, 5, 4] },
  { key: "data", title: "landing.faqGroupData", numbers: [6, 3, 7] },
] as const;

const HOW_STEPS = [
  { n: 1, title: "landing.step1Title", body: "landing.step1Body" },
  { n: 2, title: "landing.step2Title", body: "landing.step2Body" },
  { n: 3, title: "landing.step3Title", body: "landing.step3Body" },
] as const;

interface FaqGroup {
  key: string;
  title: string | null;
  items: { key: string; question: string; answer: string }[];
}

function LandingFaq({ groups }: { groups: FaqGroup[] }) {
  const { t } = useTranslation();
  return (
    <section className="landing__section">
      <h2 className="landing__section-title">{t("landing.faqTitle")}</h2>
      <div className="landing__faq">
        {groups.map((group) => (
          <div key={group.key} className="landing__faq-group">
            {group.title && <h3 className="landing__faq-group-title">{t(group.title)}</h3>}
            <dl className="landing__faq-list">
              {group.items.map((item) => (
                <div key={item.key} className="landing__faq-item">
                  <dt className="landing__faq-q">{item.question}</dt>
                  <dd className="landing__faq-a">{item.answer}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
      <Link to="/learn" className="landing__link landing__faq-more">
        {t("landing.learnMore")}
      </Link>
    </section>
  );
}

// Apple's official badge, unaltered and at its minimum on-screen height (40px).
function AppStoreBadge() {
  const { t } = useTranslation();
  return (
    <a
      href={APP_STORE_URL}
      className="landing__store-badge"
      target="_blank"
      rel="noopener noreferrer"
    >
      <img src="/images/app-store-badge.svg" alt={t("landing.appStoreBadgeAlt")} height={40} />
    </a>
  );
}

function IphoneSection() {
  const { t } = useTranslation();
  return (
    <section className="landing__section landing__row">
      <div>
        <h2 className="landing__section-title">{t("landing.iosTitle")}</h2>
        <p className="landing__prose">{t("landing.iosBody")}</p>
        <AppStoreBadge />
      </div>
      <figure className="landing__art landing__phone">
        <picture>
          <source srcSet="/images/iphone-timeline.webp" type="image/webp" />
          <img
            className="landing__phone-screen"
            src="/images/iphone-timeline.jpg"
            alt={t("landing.iosShotAlt")}
            width={560}
            height={1217}
            loading="lazy"
            decoding="async"
          />
        </picture>
        <figcaption className="landing__shot-caption">{t("landing.iosShotCaption")}</figcaption>
      </figure>
    </section>
  );
}

export default function LandingPage() {
  const { t, i18n } = useTranslation();
  const authed = !!getAccessToken();
  const lang = i18n.language?.startsWith("nl") ? "nl" : "en";

  // Live admin-managed FAQ, falling back to the static i18n copy when the
  // endpoint is empty or unreachable (see docs/plans/2026-06-06-faq-admin-design.md).
  const { data: faqData } = useQuery({ queryKey: ["faq"], queryFn: getFaq, enabled: !authed });
  const faqGroups = useMemo(() => {
    const entries = faqData?.entries ?? [];
    if (entries.length > 0) {
      const items = entries.map((e) => ({
        key: e.id,
        question: lang === "nl" ? e.question_nl : e.question_en,
        answer: lang === "nl" ? e.answer_nl : e.answer_en,
      }));
      return [{ key: "live", title: null, items }];
    }
    return FAQ_GROUPS.map((group) => ({
      key: group.key,
      title: group.title,
      items: group.numbers.map((n) => ({
        key: `static-${n}`,
        question: t(`landing.faqQ${n}`),
        answer: t(`landing.faqA${n}`),
      })),
    }));
  }, [faqData, lang, t]);
  const faqItems = useMemo(() => faqGroups.flatMap((group) => group.items), [faqGroups]);

  // Inject SoftwareApplication + FAQ structured data for search engines.
  useEffect(() => {
    if (authed) return;
    const origin = window.location.origin;
    const jsonLd = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          name: t("app.title"),
          url: origin,
          logo: `${origin}/favicon.svg`,
        },
        {
          "@type": "WebSite",
          name: t("app.title"),
          url: origin,
        },
        {
          "@type": "SoftwareApplication",
          name: t("app.title"),
          applicationCategory: "LifestyleApplication",
          operatingSystem: "Web, iOS",
          description: t("landing.about"),
        },
        {
          "@type": "FAQPage",
          mainEntity: faqItems.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: { "@type": "Answer", text: item.answer },
          })),
        },
      ],
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);
    return () => {
      script.remove();
    };
  }, [authed, t, faqItems]);

  // Reveal sections as they scroll into view; one gentle, orchestrated system.
  // Reduced-motion users see everything immediately (the CSS only animates
  // under no-preference), as do environments without IntersectionObserver.
  useEffect(() => {
    if (authed) return;
    const sections = document.querySelectorAll(".landing__sections .landing__section");
    if (typeof IntersectionObserver === "undefined") {
      for (const s of sections) s.classList.add("is-revealed");
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");
            io.unobserve(entry.target);
          }
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.08 },
    );
    for (const s of sections) io.observe(s);
    return () => io.disconnect();
  }, [authed]);

  // Gentle hero parallax: the photo recedes slower than the page scrolls.
  // Transform-only (no layout shift), rAF-throttled, off under reduced motion.
  useEffect(() => {
    if (authed) return;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    if (reduced) return;
    const scroller = document.querySelector(".landing");
    const imgs = document.querySelectorAll<HTMLElement>(".landing__hero-img");
    if (!scroller || imgs.length === 0) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      const shift = Math.min(scroller.scrollTop * 0.25, 280);
      for (const img of imgs) {
        img.style.transform = `translate3d(0, ${shift}px, 0) scale(1.1)`;
      }
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    scroller.addEventListener("scroll", onScroll, { passive: true });
    update();
    return () => {
      scroller.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [authed]);

  // When the beta is full the way in is the waitlist, so say so on the
  // buttons rather than after someone has chosen an encryption key.
  const registrationClosed = useRegistrationOpen() === false;
  const { theme } = useTheme();
  const primaryCta = registrationClosed
    ? { to: "/waitlist", label: t("landing.ctaWaitlist") }
    : { to: "/register", label: t("landing.ctaCreate") };

  // Logged-in visitors skip the marketing page.
  if (authed) {
    return <Navigate to="/trees" replace />;
  }

  return (
    <div className="landing">
      <section className="landing__hero">
        {/* Only the active theme's photo, so the other is never downloaded.
            It is the LCP element, so fetch it first. */}
        <picture>
          <source srcSet={`/images/hero-${theme}.webp`} type="image/webp" />
          <img
            className={`landing__hero-img landing__hero-img--${theme}`}
            src={`/images/hero-${theme}.jpg`}
            alt=""
            decoding="async"
            fetchPriority="high"
          />
        </picture>
        <AmbientBackground />
        <div className="landing__hero-shell">
          <header className="landing__hero-content">
            <h1 className="landing__hero-title">{t("app.title")}</h1>
            <p className="landing__hero-message">{t("landing.heroTagline")}</p>
            <p className="landing__hero-lede">{t("landing.heroIntro")}</p>
            <p className="landing__hero-beta">
              {registrationClosed ? t("landing.betaNote") : t("landing.betaNoteOpen")}
            </p>
            <div className="landing__cta-row">
              <Link to={primaryCta.to} className="landing__cta landing__cta--primary">
                {primaryCta.label}
              </Link>
              <Link to="/login" className="landing__cta landing__cta--ghost">
                {t("landing.ctaLogin")}
              </Link>
              <a href="#how" className="landing__cta-quiet">
                {t("landing.ctaHow")}
              </a>
            </div>
            <AppStoreBadge />
          </header>
          <div className="landing__hero-art">
            <Glimpse name="tree" alt={t("landing.shotTreeAlt")} />
          </div>
        </div>
        <div className="landing__scroll-cue" aria-hidden="true" />
      </section>

      <div className="landing__sections">
        <GrowingBranch />
        <section className="landing__section landing__row">
          <div>
            <h2 className="landing__section-title">{t("landing.whatTitle")}</h2>
            <p className="landing__prose">{t("landing.whatBody")}</p>
          </div>
          <figure className="landing__art">
            <Glimpse name="timeline" alt={t("landing.shotTimelineAlt")} />
            <figcaption className="landing__shot-caption">
              {t("landing.shotTimelineCaption")}
            </figcaption>
          </figure>
        </section>

        <section className="landing__section">
          <h2 className="landing__section-title">{t("landing.whoTitle")}</h2>
          <p className="landing__prose">{t("landing.whoBody")}</p>
          <p className="landing__prose">{t("landing.whoClinician")}</p>
          <Link to="/genogram" className="landing__link">
            {t("landing.genogramLink")}
          </Link>
        </section>

        <section className="landing__section" id="how">
          <h2 className="landing__section-title">{t("landing.howTitle")}</h2>
          <ol className="landing__steps">
            {HOW_STEPS.map((step) => (
              <li key={step.n} className="landing__step">
                <span className="landing__step-num" aria-hidden="true">
                  {step.n}
                </span>
                <h3 className="landing__step-title">{t(step.title)}</h3>
                <p className="landing__prose">{t(step.body)}</p>
              </li>
            ))}
          </ol>
          <p className="landing__after-steps">
            <Link to="/demo" className="landing__link">
              {t("landing.liveDemo")}
            </Link>
            <Link to="/tour" className="landing__link">
              {t("landing.tourLink")}
            </Link>
          </p>
        </section>

        <section className="landing__section landing__row landing__row--flip">
          <div>
            <h2 className="landing__section-title">{t("landing.privacyHeading")}</h2>
            <p className="landing__glance">{t("landing.privacyGlance")}</p>
            <p className="landing__prose">{t("landing.privacy")}</p>
            <p className="landing__prose">{t("landing.openSource")}</p>
            <div className="landing__links">
              <Link to="/security" className="landing__link">
                {t("security.link")}
              </Link>
              <Link to="/privacy" className="landing__link">
                {t("landing.readPrivacyPolicy")}
              </Link>
              <a
                href={GITHUB_URL}
                className="landing__link"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("landing.sourceLink")}
              </a>
            </div>
          </div>
          <div className="landing__art">
            <ShieldGlimpse />
          </div>
        </section>

        <IphoneSection />

        <section className="landing__section landing__founder">
          <h2 className="landing__section-title">{t("landing.founderTitle")}</h2>
          <p className="landing__prose">{t("landing.founderBody1")}</p>
          <p className="landing__prose">{t("landing.founderBody2")}</p>
          <p className="landing__prose">{t("landing.founderBody3")}</p>
          <p className="landing__founder-signature">{t("landing.founderSignature")}</p>
        </section>

        <LandingFaq groups={faqGroups} />

        <section className="landing__section landing__final">
          <h2 className="landing__section-title">{t("landing.finalCtaTitle")}</h2>
          <p className="landing__prose">
            {registrationClosed ? t("landing.finalCtaBodyFull") : t("landing.finalCtaBody")}
          </p>
          <Link to={primaryCta.to} className="landing__cta landing__cta--primary">
            {primaryCta.label}
          </Link>
        </section>
      </div>
    </div>
  );
}
